// פאנלים לאתר הדמו: חבילות (כולל העלאת ZIP חיצוני) ובדיקה אוטומטית עם טבלת תוצאות.
import { useState } from 'react';
import { api, waitForJob, type Job } from '../../api';
import { ErrorBox, Field, fmtDate, JobBadge, useLoad, useToast } from '../ui';
import type { DemoPackage } from './DemoSlot';

export function PackagesPanel({ packages, reload }: { packages: DemoPackage[] | null; reload: () => void }) {
  const toast = useToast();
  const clients = useLoad<any[]>('/api/clients');
  const [file, setFile] = useState<File | null>(null);
  const [label, setLabel] = useState('');
  const [version, setVersion] = useState('');
  const [clientId, setClientId] = useState('');
  const [w, setW] = useState('');
  const [h, setH] = useState('');
  const [busy, setBusy] = useState(false);
  const [fileKey, setFileKey] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, any[]>>({});

  const upload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) { setErr('יש לבחור קובץ ZIP'); return; }
    setBusy(true); setErr(null);
    const fd = new FormData();
    if (clientId) fd.append('clientId', clientId);
    fd.append('label', label.trim() || file.name.replace(/\.zip$/i, ''));
    if (version) fd.append('version', version);
    if (w) fd.append('width', w);
    if (h) fd.append('height', h);
    fd.append('file', file);
    try {
      await api.upload('/api/demo-packages/upload', fd);
      toast('החבילה הועלתה');
      setFile(null); setLabel(''); setVersion('');
      setFileKey((k) => k + 1);
    } catch (x: any) { setErr(x.message); } finally { setBusy(false); reload(); }
  };
  const loadResults = async (id: string) => {
    try { const r = await api.get<any[]>(`/api/demo-test-results?demoPackageId=${id}`); setResults((m) => ({ ...m, [id]: r })); } catch (x: any) { toast(x.message, 'err'); }
  };

  return (
    <section className="card" aria-labelledby="pk-h">
      <h2 id="pk-h" style={{ marginTop: 0 }}>חבילות</h2>
      <p className="small muted">חבילה נוספת מתוך מרכז היצוא ("העלה לדמו") או כ-ZIP חיצוני לבדיקה ידנית.</p>
      {!packages ? <span className="spinner" /> : packages.length === 0 ? <div className="small muted">אין חבילות עדיין.</div> : (
        <ul className="iss-list">
          {packages.map((p) => (
            <li key={p.id} className="pgroup" style={{ margin: 0 }}>
              <div className="row between"><strong className="small">{p.label}</strong><span className="mono small">{p.width}×{p.height}</span></div>
              <div className="row small">
                <span className={`badge ${p.status === 'ready' ? 'ok' : 'err'}`}>{p.status === 'ready' ? 'תקין' : 'שגיאה'}</span>
                <span className="badge">{p.source === 'export' ? 'מיצוא' : 'ZIP חיצוני'}</span>
                {p.version && <span className="mono">{p.version}</span>}
                <span className="muted">{fmtDate(p.created_at)}</span>
              </div>
              {(p.errors ?? []).length > 0 && <ul className="small" style={{ color: 'var(--danger)', margin: '4px 0' }}>{p.errors!.map((e: any, i: number) => <li key={i}>{e.message ?? String(e)}</li>)}</ul>}
              <button className="linkish small" onClick={() => loadResults(p.id)}>תוצאות בדיקה שמורות</button>
              {results[p.id] && (results[p.id].length === 0 ? <div className="small muted">אין תוצאות שמורות.</div> : (
                <ul className="small">{results[p.id].slice(0, 5).map((r) => <li key={r.id}>{fmtDate(r.created_at)} · {r.viewport} · <span className={`badge ${r.passed ? 'ok' : 'err'}`}>{r.passed ? 'עבר' : 'נכשל'}</span> <span className="mono muted">{String(r.checksum).slice(0, 10)}</span></li>)}</ul>
              ))}
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={upload} className="stack" style={{ marginTop: 10 }} aria-labelledby="up-h">
        <h3 id="up-h" style={{ margin: 0 }}>העלאת ZIP חיצוני</h3>
        <Field label="קובץ ZIP"><input key={fileKey} type="file" accept=".zip,application/zip" onChange={(e) => setFile(e.target.files?.[0] ?? null)} /></Field>
        <Field label="תווית"><input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={200} /></Field>
        <div className="row">
          <Field label="גרסה"><input value={version} onChange={(e) => setVersion(e.target.value)} maxLength={60} style={{ width: 90 }} /></Field>
          <Field label="רוחב" hint="אם לא מזוהה"><input className="mono" value={w} onChange={(e) => setW(e.target.value.replace(/\D/g, ''))} style={{ width: 60 }} /></Field>
          <Field label="גובה"><input className="mono" value={h} onChange={(e) => setH(e.target.value.replace(/\D/g, ''))} style={{ width: 60 }} /></Field>
        </div>
        <Field label="לקוח">
          <select value={clientId} onChange={(e) => setClientId(e.target.value)}>
            <option value="">ללא שיוך</option>
            {(Array.isArray(clients.data) ? clients.data : []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <ErrorBox error={err} />
        <button className="btn primary" disabled={busy || !file}>{busy ? 'מעלה ובודק…' : 'העלאה'}</button>
      </form>
    </section>
  );
}

interface TestItem {
  slotName: string; position: string; label: string; version: string; exportRevision: number | null; exportKind: string | null; width: number; height: number;
  load: boolean; loadError?: string; consoleErrors: any[]; missingAssets: string[]; externalRequests: string[];
  click: { opened: string[]; expected: string | null; found: string | null; match: boolean | null; blocked: boolean; duplicate: boolean };
  finalFrameOk: boolean | null; passed: boolean;
}

export function TestPanel({ pageId, defaultViewport, lazy }: { pageId: string; defaultViewport: 'desktop' | 'mobile'; lazy: boolean }) {
  const [vp, setVp] = useState<'desktop' | 'mobile'>(defaultViewport);
  const [lz, setLz] = useState(lazy);
  const [job, setJob] = useState<Job | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const run = async () => {
    setBusy(true); setErr(null); setJob(null);
    try {
      const { jobId } = await api.post<{ jobId: string }>(`/api/demo-pages/${pageId}/test`, { viewport: vp, lazy: lz });
      const j = await waitForJob(jobId, setJob);
      if (j.status !== 'succeeded') setErr(j.error ?? 'הבדיקה נכשלה');
    } catch (x: any) { setErr(x.message); } finally { setBusy(false); }
  };
  const items: TestItem[] = job?.status === 'succeeded' ? job.result?.results ?? [] : [];
  const yes = (b: boolean | null | undefined) => b == null ? <span className="muted">—</span> : b ? <span className="badge ok">כן</span> : <span className="badge err">לא</span>;
  return (
    <section className="card" aria-labelledby="test-h">
      <h2 id="test-h" style={{ marginTop: 0 }}>בדיקה אוטומטית</h2>
      <p className="small muted">כל באנר נטען מה-sandbox בתוך iframe במידותיו. הקליק נלכד בלי לבקר בדף הנחיתה ומושווה לכתובת שננעלה ביצוא. התוצאה נשמרת לפי checksum ו-viewport.</p>
      <div className="row">
        <Field label="viewport"><select value={vp} onChange={(e) => setVp(e.target.value as any)}><option value="desktop">דסקטופ</option><option value="mobile">מובייל</option></select></Field>
        <label className="chk"><input type="checkbox" checked={lz} onChange={(e) => setLz(e.target.checked)} /> lazy loading</label>
        <button className="btn primary" onClick={run} disabled={busy}>{busy ? 'בודק…' : 'הרץ בדיקה'}</button>
        {job && <JobBadge status={job.status} />}
      </div>
      <ErrorBox error={err} />
      {job?.status === 'succeeded' && (items.length === 0 ? <div className="small muted">אין באנרים משובצים בדף.</div> : (
        <div style={{ overflowX: 'auto' }}>
          <div className={`alert ${job.result?.passed ? 'ok' : 'warn'} small`} style={{ margin: '8px 0' }}>{job.result?.passed ? 'כל הבאנרים עברו' : 'יש באנרים שנכשלו'} · viewport: {job.result?.viewport}{job.result?.lazy ? ' · lazy' : ''}</div>
          <table className="table">
            <thead><tr><th>מיקום</th><th>חבילה (מדיה/מיקום)</th><th>מידות</th><th>גרסת יצוא</th><th>טעינה</th><th>שגיאות console</th><th>נכסים חסרים</th><th>קליק</th><th>כתובת שנמצאה / צפויה</th><th>כפול / חסום</th><th>פריים סיום</th><th>תוצאה</th></tr></thead>
            <tbody>
              {items.map((r, i) => (
                <tr key={i}>
                  <td className="small">{r.slotName}</td>
                  <td className="small">{r.label}</td>
                  <td className="mono">{r.width}×{r.height}</td>
                  <td className="small">{r.exportRevision != null ? <span className="mono">r{r.exportRevision} · {r.exportKind}</span> : <>ZIP חיצוני{r.version ? ` ${r.version}` : ''}</>}</td>
                  <td>{yes(r.load)}{r.loadError && <div className="small" style={{ color: 'var(--danger)' }}>{r.loadError}</div>}</td>
                  <td>{r.consoleErrors.length ? <details><summary className="badge err">{r.consoleErrors.length}</summary><ul className="small mono">{r.consoleErrors.map((c: any, k: number) => <li key={k}>{typeof c === 'string' ? c : c.text ?? JSON.stringify(c)}</li>)}</ul></details> : <span className="badge ok">0</span>}</td>
                  <td>{r.missingAssets.length ? <details><summary className="badge err">{r.missingAssets.length}</summary><ul className="small mono">{r.missingAssets.map((c, k) => <li key={k}>{c}</li>)}</ul></details> : <span className="badge ok">0</span>}{r.externalRequests.length > 0 && <div className="small" style={{ color: 'var(--warn)' }}>{r.externalRequests.length} בקשות חיצוניות</div>}</td>
                  <td>{r.click.match == null ? <span className="badge">ללא יעד צפוי</span> : r.click.match ? <span className="badge ok">תואם</span> : <span className="badge err">לא תואם</span>}</td>
                  <td className="mono small" style={{ wordBreak: 'break-all', minWidth: 200 }}>
                    <div>נמצא: {r.click.found ?? '—'}</div>
                    <div className="muted">צפוי: {r.click.expected ?? '—'}</div>
                  </td>
                  <td className="small">{r.click.duplicate ? <span className="badge err">כפול</span> : null} {r.click.blocked ? <span className="badge err">חסום</span> : null}{!r.click.duplicate && !r.click.blocked && <span className="badge ok">תקין</span>}</td>
                  <td>{yes(r.finalFrameOk)}</td>
                  <td>{r.passed ? <span className="badge ok">עבר</span> : <span className="badge err">נכשל</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </section>
  );
}
